import React, { useState, useEffect } from 'react';
import { Button, Drawer, Descriptions, Input, Space, Alert, Tag, Select } from 'antd';
import AntTable from '../../components/CustomTable/AntTable';
import { getPendingReversals, getReversalDetail, getReversalsSummary, proposeReversalApprove, proposeReversalReject } from '../../services/reversalService';
import { NotificationManager } from 'react-notifications';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';

const { Search } = Input;
const { Option } = Select;

const ReversalQueueTab = () => {
  const [loading, setLoading] = useState(false);
  const [reversals, setReversals] = useState([]);
  const [error, setError] = useState(null);

  // Summary stats state
  const [stats, setStats] = useState({
    PENDING: 0,
    APPROVED: 0,
    REJECTED: 0,
    EXECUTED: 0,
    FAILED: 0,
  });

  // Filter states
  const [statusFilter, setStatusFilter] = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');

  // Drawer for Detail View
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedReversal, setSelectedReversal] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Role checking
  const [isMaker, setIsMaker] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    const rawRoles = localStorage.getItem('roles');
    let userRoles = [];
    try {
      userRoles = JSON.parse(rawRoles || '[]');
      if (!Array.isArray(userRoles)) {
        userRoles = [userRoles];
      }
    } catch {
      userRoles = rawRoles ? [rawRoles] : [];
    }
    setIsMaker(userRoles.includes('ADMIN') || userRoles.includes('SUPER_ADMIN') || userRoles.includes('OWNER'));
  }, []);

  const fetchSummary = async () => {
    try {
      const res = await getReversalsSummary();
      const data = res?.data || res;

      if (data && typeof data === 'object') {
        setStats({
          PENDING: Number(data.PENDING || 0),
          APPROVED: Number(data.APPROVED || 0),
          REJECTED: Number(data.REJECTED || 0),
          EXECUTED: Number(data.EXECUTED || 0),
          FAILED: Number(data.FAILED || 0),
        });
      }
    } catch (err) {
      console.error('Error fetching reversals summary:', err);
    }
  };

  const fetchReversals = async (page = 1, size = 10) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: page - 1,
        size,
      };
      if (statusFilter) params.status = statusFilter;
      if (userIdFilter) params.userId = userIdFilter;

      const res = await getPendingReversals(params);

      // Spring page support
      const content = res?.data?.content || res?.content || res?.data || (Array.isArray(res) ? res : []);
      const totalElements = res?.data?.totalElements || res?.totalElements || res?.total || content.length;

      const formatted = content.map((item, index) => ({
        ...item,
        id: item.id || item.reference || `rev_${index}`,
      }));

      setReversals(formatted);
      setPagination({
        current: page,
        pageSize: size,
        total: totalElements,
      });
    } catch (err) {
      setError(err.message || 'Failed to fetch pending reversals.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    fetchReversals(1, 10);
  }, [statusFilter, userIdFilter]);

  const handleTableChange = ({ pagination: newPagination }) => {
    fetchReversals(newPagination.current, newPagination.pageSize);
  };

  const handleSearchUserId = (val) => {
    setUserIdFilter(val.trim());
  };

  const handleInspect = async (record) => {
    setDetailLoading(true);
    setDrawerVisible(true);
    const lookupKey = record.reference || record.id;
    try {
      if (lookupKey) {
        const detailRes = await getReversalDetail(lookupKey);
        if (detailRes && (detailRes.data || detailRes.success)) {
          setSelectedReversal(detailRes.data || detailRes);
        } else {
          setSelectedReversal(record);
        }
      } else {
        setSelectedReversal(record);
      }
    } catch (err) {
      console.error('Error fetching reversal details, falling back to table record:', err);
      setSelectedReversal(record);
    } finally {
      setDetailLoading(false);
    }
  };

  // Maker propose approve
  const handleProposeApprove = (record) => {
    const key = record.reference || record.id;
    Swal.fire({
      title: 'Propose Reversal Approval',
      text: `Are you sure you want to propose approval for reversal: ${key}?`,
      icon: 'question',
      input: 'textarea',
      inputPlaceholder: 'Enter reversal reason here (required)...',
      showCancelButton: true,
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Approve',
      preConfirm: (inputValue) => {
        if (!inputValue || !inputValue.trim()) {
          Swal.showValidationMessage('Approval reason is required');
        }
        return inputValue;
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await proposeReversalApprove(key, result.value);
          if (res && (res.success || res.status === true)) {
            NotificationManager.success(res.message || 'Reversal approval proposed successfully');
            setDrawerVisible(false);
            fetchSummary();
            fetchReversals(pagination.current, pagination.pageSize);
          } else {
            NotificationManager.error(res?.message || 'Failed to propose reversal approval');
          }
        } catch (err) {
          NotificationManager.error(err.message || 'An error occurred during proposal');
        }
      }
    });
  };

  // Maker propose reject
  const handleProposeReject = (record) => {
    const key = record.reference || record.id;
    Swal.fire({
      title: 'Propose Reversal Rejection',
      text: `Are you sure you want to propose rejection for reversal: ${key}?`,
      icon: 'warning',
      input: 'textarea',
      inputPlaceholder: 'Enter rejection comment here (required)...',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Reject',
      preConfirm: (inputValue) => {
        if (!inputValue || !inputValue.trim()) {
          Swal.showValidationMessage('Rejection comment is required');
        }
        return inputValue;
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await proposeReversalReject(key, result.value);
          if (res && (res.success || res.status === true)) {
            NotificationManager.success(res.message || 'Reversal rejection proposed successfully');
            setDrawerVisible(false);
            fetchSummary();
            fetchReversals(pagination.current, pagination.pageSize);
          } else {
            NotificationManager.error(res?.message || 'Failed to propose reversal rejection');
          }
        } catch (err) {
          NotificationManager.error(err.message || 'An error occurred during proposal');
        }
      }
    });
  };

  const getStatusTagColor = (status) => {
    const val = String(status || '').toUpperCase();
    if (val === 'APPROVED' || val === 'SUCCESS') return 'success';
    if (val === 'REJECTED' || val === 'FAILED') return 'error';
    return 'warning'; // PENDING / CBA queue
  };

  const columns = [
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Transaction ID',
      dataIndex: 'transactionId',
      key: 'transactionId',
      render: (text) => text || 'N/A',
    },
    {
      title: 'Type',
      dataIndex: 'transactionType',
      key: 'transactionType',
      render: (text) => text || 'N/A',
    },
    {
      title: 'User ID',
      dataIndex: 'userId',
      key: 'userId',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (val, record) => {
        const currency = record.currency || 'NGN';
        return val != null ? `${currency} ${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : 'N/A';
      },
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      render: (text) => text || 'N/A',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusTagColor(status)} style={{ fontWeight: 600 }}>
          {status || 'PENDING'}
        </Tag>
      ),
    },
    {
      title: 'Date Logged',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => (date ? dayjs(date).format('YYYY-MM-DD HH:mm') : 'N/A'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const status = String(record.status || 'PENDING').toUpperCase();
        const canAction = isMaker && (status === 'PENDING' || status === 'FAILED' || status === 'PENDING_MAKER');
        return (
          <Space size="small">
            <Button type="default" size="small" onClick={() => handleInspect(record)}>
              Inspect
            </Button>

            <>
              <Button type="primary" size="small" style={{ backgroundColor: '#28a745', borderColor: '#28a745' }} onClick={() => handleProposeApprove(record)}>
                Approve
              </Button>
              <Button type="primary" danger size="small" onClick={() => handleProposeReject(record)}>
                Reject
              </Button>
            </>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

      {/* Summary stats */}
      <div className="stats-container" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px', marginBottom: '24px' }}>
        <div className="stat-card pending">
          <div className="stat-card-content">
            <span className="stat-card-title">PENDING</span>
            <span className="stat-card-value">{stats.PENDING}</span>
          </div>
        </div>
        <div className="stat-card approved">
          <div className="stat-card-content">
            <span className="stat-card-title">APPROVED</span>
            <span className="stat-card-value">{stats.APPROVED}</span>
          </div>
        </div>
        <div className="stat-card rejected">
          <div className="stat-card-content">
            <span className="stat-card-title">REJECTED</span>
            <span className="stat-card-value">{stats.REJECTED}</span>
          </div>
        </div>
        <div className="stat-card total">
          <div className="stat-card-content">
            <span className="stat-card-title">EXECUTED</span>
            <span className="stat-card-value">{stats.EXECUTED}</span>
          </div>
        </div>
        <div className="stat-card rejected" style={{ borderLeft: '4px solid #cf1322' }}>
          <div className="stat-card-content">
            <span className="stat-card-title">FAILED</span>
            <span className="stat-card-value">{stats.FAILED}</span>
          </div>
        </div>
      </div>

      <div className="reversal-toolbar">
        <div className="reversal-filters">
          <Select
            placeholder="Filter by Status"
            allowClear
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 180 }}
          >
            <Option value="PENDING">PENDING</Option>
            <Option value="SUCCESS">SUCCESS</Option>
            <Option value="FAILED">FAILED</Option>
          </Select>

          <Search
            placeholder="Search by User ID..."
            allowClear
            onSearch={handleSearchUserId}
            onChange={(e) => handleSearchUserId(e.target.value)}
            style={{ width: 250 }}
          />
        </div>

        <Button type="default" onClick={() => { fetchSummary(); fetchReversals(pagination.current, pagination.pageSize); }} loading={loading}>
          Refresh
        </Button>
      </div>

      <AntTable
        loading={loading}
        users={reversals}
        pagination={pagination}
        handleGetNext={handleTableChange}
        columns={columns}
      />

      <Drawer
        title={`Reversal Details: ${selectedReversal?.reference || selectedReversal?.id || ''}`}
        placement="right"
        width={650}
        onClose={() => setDrawerVisible(false)}
        visible={drawerVisible}
        className="reversal-detail-drawer"
        extra={
          selectedReversal && isMaker && ['PENDING', 'FAILED', 'PENDING_MAKER'].includes(String(selectedReversal.status || 'PENDING').toUpperCase()) && (
            <Space>
              <Button type="primary" style={{ backgroundColor: '#28a745', borderColor: '#28a745' }} onClick={() => handleProposeApprove(selectedReversal)}>
                Approve
              </Button>
              <Button type="primary" danger onClick={() => handleProposeReject(selectedReversal)}>
                Reject
              </Button>
            </Space>
          )
        }
      >
        {detailLoading ? (
          <div>Loading details...</div>
        ) : selectedReversal ? (
          <div className="reversal-detail-container">
            <div className="reversal-detail-section">
              <h4>Reversal Information</h4>
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Reference">{selectedReversal.reference || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Transaction ID">{selectedReversal.transactionId || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Source">{selectedReversal.source || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Transaction Type">{selectedReversal.transactionType || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Routing Path">{selectedReversal.routingPath || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={getStatusTagColor(selectedReversal.status)}>
                    {selectedReversal.status || 'PENDING'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Date Created">
                  {selectedReversal.createdAt ? dayjs(selectedReversal.createdAt).format('YYYY-MM-DD HH:mm:ss') : 'N/A'}
                </Descriptions.Item>
              </Descriptions>
            </div>

            <div className="reversal-detail-section">
              <h4>Transaction Details</h4>
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="User ID">{selectedReversal.userId || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="From Account">{selectedReversal.fromAccountNumber || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="To Account">{selectedReversal.toAccountNumber || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Receiver Bank Code">{selectedReversal.receiverBankCode || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Amount">
                  {selectedReversal.amount != null ? `${selectedReversal.currency || 'NGN'} ${Number(selectedReversal.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Fee">
                  {selectedReversal.fee != null ? `${selectedReversal.currency || 'NGN'} ${Number(selectedReversal.fee).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Narration">{selectedReversal.narration || 'N/A'}</Descriptions.Item>
                {selectedReversal.metadata && (
                  <Descriptions.Item label="Metadata">
                    <pre style={{ fontSize: '11px', margin: 0 }}>
                      {typeof selectedReversal.metadata === 'object'
                        ? JSON.stringify(selectedReversal.metadata, null, 2)
                        : String(selectedReversal.metadata)}
                    </pre>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </div>
          </div>
        ) : (
          <div>No record selected</div>
        )}
      </Drawer>
    </div>
  );
};

export default ReversalQueueTab;
